import { EmailIcon } from "@chakra-ui/icons";
import { Badge, Box, Button, Card, CardBody, Checkbox, CheckboxGroup, Divider, Heading, HStack, IconButton, Input, Spacer, Stack, StackDivider, Text, useToast } from "@chakra-ui/react";
import { use } from "i18next";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { MdChecklist } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { patchRole, patchRolePermissions } from "service/api/role.api";
import { updateRole, updateRolePermissions } from "store/reducers/role.slice";
import { PermissionModalMode } from "types/PermissionModalMode";
import { SubTarget } from "types/SubTarget";
import { Target } from "types/Target";
import { checkIfIndicatorIsInRolePermissions, checkIfIndicatorIsInRolesArray, generateBothDefaultIdIndicatorsPermission, getIndicatorsIndexList } from "utils/utils";
import TargetFilter from "views/admin/target/components/targetFilter/TargetFilter";

type PermissionsFormProps = {
  documentId: string;
  target: Target,
  subTarget: SubTarget;
  modalMode: PermissionModalMode;
  roleTitle: string;
  roleDescription: string;
  roleCode: string;
  roleId: string;
  rolePermissions: any[];
  onClose: () => any;
};


/**
 * PermissionsForm component handles the display and submission of role permissions for a specific document and sub-target.
 * 
 * @param {PermissionsFormProps} props - The properties for the PermissionsForm component.
 * @param {string} props.documentId - The ID of the document.
 * @param {any} props.target - The target object.
 * @param {any} props.subTarget - The sub-target object containing actions and indicators.
 * @param {PermissionModalMode} props.modalMode - The mode of the modal, either 'Edit' or 'View'.
 * @param {string} props.roleId - The ID of the role.
 * @param {any[]} props.rolePermissions - The permissions associated with the role.
 * @param {Function} props.onClose - The function to call when the modal is closed.
 * 
 * @returns {JSX.Element} The rendered PermissionsForm component.
 * 
 * @component
 * 
 * @example
 * <PermissionsForm
 *   documentId="123"
 *   target={targetObject}
 *   subTarget={subTargetObject}
 *   modalMode={PermissionModalMode.Edit}
 *   roleId="roleId123"
 *   rolePermissions={rolePermissionsArray}
 *   onClose={handleClose}
 * />
 * 
 * @function
 * @name PermissionsForm
 */
export default function PermissionsForm({ documentId, target, subTarget, modalMode, roleId, rolePermissions, onClose }: PermissionsFormProps) {

  const dispatch = useDispatch()
  const toast = useToast()
  const { register, handleSubmit, watch, getValues, setValue } = useForm({});
  const isFormEditable = modalMode === PermissionModalMode.Edit ? true : false;
  const watchedFields = watch();

  const onSubmit: SubmitHandler<any> = (data) => {
    if (modalMode === PermissionModalMode.Edit) {

      const permissionsValues = getIndicatorsIndexList(data, "editValueIndicator")
      const permissionsAttachments = getIndicatorsIndexList(data, "editAttachmentsIndicator")
      patchRolePermissions(roleId, documentId, subTarget?._id, permissionsValues, permissionsAttachments).then((res) => {

        dispatch(updateRolePermissions({ roleId: roleId, permissions: res.permissions }))

        toast({
          title: 'Ruolo aggiornato con successo.',
          description: "",
          status: 'success',
          duration: 9000,
          isClosable: true,
          position: "top"
        })


      }).catch((err) => {
        toast({
          title: 'Errore aggiornamento ruolo.',
          description: err.message,
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: "top"
        })
      })
    }
    onClose()
  }

  const markAllAsChecked = () => {
    subTarget?.actions.forEach(action => {
      action.indicators.forEach(indicator => {
        setValue(`editValueIndicator-${indicator?._id}`, true);
        setValue(`editAttachmentsIndicator-${indicator?._id}`, true);
      });
    });
  };



  return (
    subTarget &&

    <form onSubmit={handleSubmit(onSubmit)}>

      <HStack>
        <Heading as="h2" size="lg">
          Sotto-obiettivo {subTarget?.index}
        </Heading>
        <Spacer />
        <Button leftIcon={<MdChecklist />} colorScheme='blue' variant='solid' size={"sm"} onClick={markAllAsChecked}>
          Seleziona tutti gli indicatori
        </Button>
      </HStack>

      <Text align={"left"}> {subTarget?.title}</Text>
      <Stack divider={<StackDivider />} spacing="4">

        {subTarget?.actions.map((action) => {
          return (
            <>
              <Card variant={"filled"}>
                <CardBody>
                  <Box>
                    <Badge
                      colorScheme={"blue"}
                      fontSize="1.2em"
                      as="h3"
                      textTransform="uppercase"
                    >
                      Azione {action.index}
                    </Badge>
                    <Text>{action.title}.</Text>
                  </Box>
                  {
                    action.indicators.map((indicator) => {
                      return (
                        <Box mt={3} bgColor={"white"} p={1}>
                          <Heading size="xs" as="h4" textTransform="uppercase">
                            Indicatore {indicator?.index} - {indicator?.title}
                          </Heading>
                          <CheckboxGroup>
                            <Stack spacing={1} direction='column'>
                              <Checkbox isReadOnly={!isFormEditable}

                                //defaultChecked={checkIfIndicatorIsInRolePermissions(documentId, subTarget?._id, indicator?._id, rolePermissions, true )}
                                isChecked={watchedFields[`editValueIndicator-${indicator?._id}`] ?? checkIfIndicatorIsInRolePermissions(documentId, subTarget?._id, indicator?._id, rolePermissions, true)}
                                {
                                ...register(`editValueIndicator-${indicator?._id}`)
                                }
                              >
                                Valore modificabile
                              </Checkbox>
                              <Checkbox isReadOnly={!isFormEditable}
                                //defaultChecked={checkIfIndicatorIsInRolePermissions(documentId, subTarget?._id, indicator?._id, rolePermissions, false)}
                                isChecked={watchedFields[`editAttachmentsIndicator-${indicator?._id}`] ?? checkIfIndicatorIsInRolePermissions(documentId, subTarget?._id, indicator?._id, rolePermissions, true)}
                                {
                                ...register(`editAttachmentsIndicator-${indicator?._id}`)
                                }
                              >
                                Allegati modificabili
                              </Checkbox>
                            </Stack>
                          </CheckboxGroup>
                        </Box>
                      );
                    })
                  }
                  <Divider orientation='horizontal' />
                </CardBody>
              </Card>
            </>
          );
        })}

        {isFormEditable && (
          <Button variant={"solid"} colorScheme="green" type="submit">
            Salva modifiche
          </Button>
        )}
      </Stack>
    </form >
  );
}
